import { toPairs, filter } from "ramda";
import { debounce } from "lodash-es";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { isMobile } from "react-device-detect";

import { LoadingContext } from "app/components/providers/loadingProvider";
import { formatOptions } from "app/(new-pages)/components/allSelect/helper";

import { useFirstRender } from "helpers/useFirstRender";
import { useSuggest } from "../../hook/index";
import { zipLocation } from "../../service";
import { convertSpecialCharacters } from "helpers/utilities";
import { v2Encode } from "../../interpreters/encoder";
import { convertToHtmlUrl } from "helpers/converUrl";

export default function useSearch(config, searchParams, params) {
  const { lang = "en-us" } = params;
  const firstRender = useFirstRender();
  const { push } = useContext(LoadingContext);
  const [suggestionList, handleSuggestionSearch, addSearchHistory] = useSuggest() as any[];

  const { location_filter, sort_filter, input_text, remote_only_text } = config;
  const {
    sort: init_sort,
    query: init_query,
    language: init_language,

    main_function,
    job_function,
    function_job_title,

    region,
    location,

    parentBenefits,
    childrenBenefits,

    salary: path_salary,
    salary_type: path_salaryType,

    company_financing_stage,
    company_size,
    degree,
    job_type,
    xp_lvl,
    company_industry
  } = searchParams;

  const [sort, setSort] = useState(init_sort ? [init_sort] : [2]);
  const sortFilter = formatOptions(sort_filter, []);

  const [query, setQuery] = useState(init_query || "");

  const [jobType, setJobType] = useState(job_type ? job_type.split(",") : []);
  const [salary, setSalary] = useState({
    salary: path_salary ? path_salary.split(",") : [],
    salary_type: path_salaryType ? path_salaryType.split(",") : []
  });

  const [locationValue, setLocationValue] = useState({
    region: region ? region.split(",") : [],
    location: location ? location.split(",") : []
  });

  const [benefits, setBenefits] = useState({
    category_value: parentBenefits ? parentBenefits.split(",") : [],
    benefits: childrenBenefits ? childrenBenefits.split(",") : []
  });

  const [jobFunction, setJobFunction] = useState({
    mainFunctions: main_function ? main_function.split(",") : [],
    jobFunctions: job_function ? job_function.split(",") : [],
    functionTitles: function_job_title ? function_job_title.split(",") : []
  });

  const [moreFilterData, setMoreFilterData] = useState({
    company_financing_stage: company_financing_stage ? company_financing_stage : "",
    company_size: company_size ? company_size : "",
    degree: degree ? degree : "",
    xp_lvl: xp_lvl ? xp_lvl : "",
    company_industry: company_industry ? company_industry : ""
  });

  const [selectCount, setSelectCount] = useState(0);

  const debouncedSearch = useCallback(
    debounce((query) => handleSuggestionSearch(query), 300),
    []
  );

  useEffect(() => {
    const count = [
      ...moreFilterData.company_financing_stage?.split(","),
      ...moreFilterData.company_size?.split(","),
      ...moreFilterData.degree?.split(","),
      ...moreFilterData.xp_lvl?.split(","),
      ...moreFilterData.company_industry?.split(",")
    ].filter(Boolean);
    setSelectCount(count.length);
  }, [moreFilterData]);

  useEffect(() => {
    debouncedSearch(query);
  }, [query, debouncedSearch]);

  const filterParams = useMemo(() => {
    const keyWords = query?.trim?.().replace(/\s+/g, " ");
    return filter((a) => a !== null && a !== undefined && a != "")({
      query: convertSpecialCharacters(keyWords),
      page: "1",
      sort: sort?.join(","),
      jobType: jobType ?? [],

      region: locationValue.region ?? [],
      location: locationValue.location ?? [],

      mainFunctions: jobFunction.mainFunctions ?? [],
      jobFunctions: jobFunction.jobFunctions ?? [],
      functionTitles: jobFunction.functionTitles ?? [],

      parentBenefits: benefits.category_value ?? [],
      childrenBenefits: benefits.benefits ?? [],

      salary: salary.salary ?? [],
      salaryType: salary.salary_type ?? [],

      financingStages: moreFilterData.company_financing_stage?.split(",") ?? [],
      companySizes: moreFilterData.company_size?.split(",") ?? [],
      qualification: moreFilterData.degree?.split(",") ?? [],
      workExperience: moreFilterData.xp_lvl?.split(",") ?? [],
      industry: moreFilterData.company_industry?.split(",") ?? []
    });
  }, [sort, locationValue, jobFunction, query, benefits, moreFilterData, salary, jobType]);

  const result = useMemo(() => {
    return v2Encode(filterParams);
  }, [filterParams]);

  const reload = useCallback(
    async (newQuery?) => {
      if (!firstRender) {
        const queryToUse = (typeof newQuery == "string" && newQuery) || result.params?.query;
        let updatedParams: any = { ...result.params };

        if (queryToUse) {
          if (queryToUse.trim().length !== 0) {
            addSearchHistory(queryToUse);
          }
          updatedParams = { ...result.params, query: queryToUse };
        }

        const zippedParams = await zipLocation(updatedParams);
        const url = new URLSearchParams(toPairs(zippedParams)).toString();

        if (!isMobile) {
          window.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth"
          });
        }

        if (typeof newQuery == "string" && newQuery) {
          const path = "/" + lang + "/jobs-hiring/job-search" + "?" + url;
          push(path);
          convertToHtmlUrl(path);
        } else {
          const path = "/" + lang + "/jobs-hiring/" + result.searchQuery + "?" + url;
          push(path);
          convertToHtmlUrl(path);
        }
      }
    },
    [firstRender, lang, result]
  );

  useEffect(() => {
    reload();
  }, [sort, locationValue, jobFunction, benefits, moreFilterData, salary, jobType]);

  const handleClearAll = useCallback(() => {
    setQuery("");
    setSort([2]);

    setLocationValue({
      region: [],
      location: []
    });

    setJobFunction({
      mainFunctions: [],
      jobFunctions: [],
      functionTitles: []
    });

    setBenefits({
      category_value: [],
      benefits: []
    });

    setMoreFilterData({
      company_financing_stage: "",
      company_size: "",
      degree: "",
      xp_lvl: "",
      company_industry: ""
    });

    setSalary({
      salary: [],
      salary_type: []
    });

    setJobType([]);

    setSelectCount(0);
  }, []);

  const handleSortReset = () => {
    setSort([2]);
  };

  return {
    location_filter,
    query,
    setQuery,
    reload,
    input_text,
    suggestionList: handleFilterSuggestionList(suggestionList),

    sortFilter,
    locationValue,
    setLocationValue,
    jobFunction,
    setJobFunction,
    handleClearAll,
    sort,
    setSort,
    remote_only_text,
    handleSortReset,
    benefits,
    setBenefits,
    moreFilterData,
    setMoreFilterData,
    selectCount,
    setSelectCount,
    salary,
    setSalary,
    jobType,
    setJobType
  };
}
export const handleFilterSuggestionList = (suggestionList = []) => {
  return suggestionList.map((item) => {
    if (item?.length && typeof item === "string") {
      return { value: item, label: item };
    }

    return {
      ...item,
      label: item?.value
    };
  });
};
